import { avaliarColocacoes, type AvaliacaoEstabilizacao, type ColocacaoEquipamento, type EstadoColocacao } from '../didatico/estabilizacao'
import { perfilVeiculoPorId } from '../didatico/perfilVeiculo'
import { mundoParaLocal, posoDoObjeto, veiculoMaisProximo, type PontoPlano } from './referencial'
import { posicaoEstabilizacaoDaPosicaoFinal } from './posicaoFinal'
import type { ObjetoTabletop } from './tipos'

/** Distância máxima (m), no plano do solo, para um equipamento contar como "deste" veículo. */
export const RAIO_ASSOCIACAO_EQUIPAMENTO = 3.2

export interface AssociacaoEquipamento {
  readonly idEquipamento: string
  readonly idVeiculo: string
}

/**
 * Liga cada equipamento de estabilização ao veículo mais próximo, dentro de
 * um raio razoável — equipamento longe demais de qualquer carro (por engano,
 * ou um cone perdido no meio do cenário) não conta para nenhuma avaliação.
 */
export function associarEquipamentosAosVeiculos(
  objetos: readonly ObjetoTabletop[],
): readonly AssociacaoEquipamento[] {
  const veiculos = objetos.filter((o) => o.veiculo)
  if (veiculos.length === 0) return []

  const associacoes: AssociacaoEquipamento[] = []
  for (const objeto of objetos) {
    if (!objeto.equipamento) continue
    const ponto: PontoPlano = { x: objeto.transformacao.posicao[0], z: objeto.transformacao.posicao[2] }
    const veiculo = veiculoMaisProximo(ponto, veiculos)
    if (!veiculo) continue
    const poso = posoDoObjeto(veiculo)
    const distancia = Math.hypot(ponto.x - poso.x, ponto.z - poso.z)
    if (distancia > RAIO_ASSOCIACAO_EQUIPAMENTO) continue
    associacoes.push({ idEquipamento: objeto.id, idVeiculo: veiculo.id })
  }
  return associacoes
}

export interface AvaliacaoEstabilizacaoPorVeiculo {
  readonly idVeiculo: string
  readonly avaliacao: AvaliacaoEstabilizacao
  /** estado de cada equipamento associado, indexado pelo id do OBJETO do Table top (não da zona) */
  readonly estadoPorEquipamento: ReadonlyMap<string, EstadoColocacao>
}

/**
 * Avalia a estabilização de cada veículo da cena, no referencial dele
 * próprio — um carro girado e deslocado é avaliado exatamente como um na
 * origem, porque o equipamento é convertido para local antes de chamar
 * `avaliarColocacoes` (a mesma função que a aba de Estabilização usa para um
 * único veículo).
 */
export function avaliarEstabilizacaoDaCena(
  objetos: readonly ObjetoTabletop[],
): readonly AvaliacaoEstabilizacaoPorVeiculo[] {
  const associacoes = associarEquipamentosAosVeiculos(objetos)
  const veiculos = objetos.filter((o) => o.veiculo)

  return veiculos.map((veiculo) => {
    const idsAssociados = associacoes
      .filter((a) => a.idVeiculo === veiculo.id)
      .map((a) => a.idEquipamento)
    const poso = posoDoObjeto(veiculo)

    const colocacoes: ColocacaoEquipamento[] = idsAssociados
      .map((id) => objetos.find((o) => o.id === id))
      .filter((o): o is ObjetoTabletop => o !== undefined && o.equipamento !== undefined)
      .map((o) => ({
        id: o.id,
        equipamento: o.equipamento!.tipo,
        posicao: mundoParaLocal({ x: o.transformacao.posicao[0], z: o.transformacao.posicao[2] }, poso),
        ...(o.equipamento!.rotacaoGraus === undefined ? {} : { rotacaoGraus: o.equipamento!.rotacaoGraus }),
        ...(o.equipamento!.comCunhaAjuste === undefined ? {} : { comCunhaAjuste: o.equipamento!.comCunhaAjuste }),
      }))

    const perfil = perfilVeiculoPorId(veiculo.veiculo!.idPerfil)
    const posicaoTreino = posicaoEstabilizacaoDaPosicaoFinal(veiculo.veiculo!.posicaoFinal)
    const avaliacao = avaliarColocacoes(posicaoTreino, perfil, colocacoes)
    const estadoPorEquipamento = new Map(avaliacao.leituras.map((l) => [l.colocacao.id, l.estado]))

    return { idVeiculo: veiculo.id, avaliacao, estadoPorEquipamento }
  })
}

export function avaliacaoDoVeiculo(
  avaliacoes: readonly AvaliacaoEstabilizacaoPorVeiculo[],
  idVeiculo: string,
): AvaliacaoEstabilizacaoPorVeiculo | null {
  return avaliacoes.find((a) => a.idVeiculo === idVeiculo) ?? null
}
